import { Stack, Typography } from '@mui/material';
import { type InventoryResource, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import { ErrorDialog } from './errorDialog';

import { FormTextField } from '../form/formTextField';
import { getCloudFieldConfig } from '../form/validation/cloudFieldValidation';
import { CopyableCode } from '../shared/copyableCode';

const managedIdentityConfig = getCloudFieldConfig(
  Provider.Azure,
  'managedIdentityResourceId'
);
const appIdConfig = getCloudFieldConfig(Provider.Azure, 'applicationId');

interface SapHanaVmActionRequiredDialogProps {
  resource: InventoryResource;
  onClose: () => void;
  onRefreshResources?: () => Promise<void>;
}

export const SapHanaVmActionRequiredDialog = ({
  resource,
  onClose,
  onRefreshResources,
}: SapHanaVmActionRequiredDialogProps) => {
  const dal = useDAL();
  const { snackbar } = useWorkspace();

  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  const [overrideAppId, setOverrideAppId] = useState<string>('');
  const [managedIdentityResourceId, setManagedIdentityResourceId] =
    useState<string>('');

  const { data: scanningAccounts } = dal.cloudAccounts.scanning.useList();
  const { data: backupApps } = dal.cloudAccounts.azureApps.useApps('Scanning');
  const agentStorageAccount = `eonsap${resource.providerAccountId.replaceAll('-', '').substring(0, 17)}`;
  const scanningAccount = scanningAccounts?.accounts.find(
    (account) => account.cloudProvider === Provider.Azure
  );

  const onValidateConfiguration = async () => {
    setIsValidating(true);
    setValidationError('');

    try {
      const response = await dal.azure.batchValidateResourceConfiguration([
        resource.id,
      ]);

      if (response.results.every((result) => result.isValid)) {
        if (onRefreshResources) {
          void onRefreshResources();
        }

        snackbar.showMessage('Configuration validated successfully.');
        onClose();
      } else {
        setValidationError(JSON.stringify(response, null, 2));
      }
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <>
      <MainDialog
        isOpen={!validationError}
        isLoading={false}
        header={{
          title: 'Complete resource configuration',
          subtitle: (
            <Typography>
              Click the button below to validate the SAP HANA configuration.
            </Typography>
          ),
          onClose,
        }}
        footer={{
          primaryButton: {
            text: 'Validate Configuration',
            onClick: onValidateConfiguration,
            'data-testid': 'sap-hana-vm-action-required-dialog-validate-button',
          },
          secondaryButton: {
            text: 'Cancel',
            onClick: onClose,
            'data-testid': 'sap-hana-vm-action-required-dialog-cancel-button',
          },
          submitInProgress: isValidating,
          loadingText: 'Checking configuration...',
        }}
        sx={{
          width: '40%',
          minWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'hidden',
        }}
      >
        <Stack
          className='px-[24px] py-0 overflow-y-auto'
          gap='16px'
          sx={{
            maxHeight: '50vh',
          }}
        >
          <Typography variant='body2' component='span' className='text-inherit'>
            Install the EON Agent on your SAP HANA VM using the script below to
            complete the configuration.
          </Typography>
          <FormTextField
            title='Application (Client) ID Override'
            placeholder={appIdConfig.placeholder}
            validation={appIdConfig.validation}
            value={overrideAppId || ''}
            onChange={(event) => setOverrideAppId(event.target.value)}
          />
          <FormTextField
            title='Managed Identity Resource ID'
            placeholder={managedIdentityConfig.placeholder}
            value={managedIdentityResourceId || ''}
            validation={managedIdentityConfig.validation}
            onChange={(event) =>
              setManagedIdentityResourceId(event.target.value)
            }
          />
          <CopyableCode
            code={`\
# Authenticate to dedicated Azure AD application using Managed Identity
MANAGED_IDENTITY_RESOURCE_ID="${managedIdentityResourceId}"
IMDS_URL="http://169.254.169.254/metadata/identity/oauth2/token?api-version=2019-08-01&resource=api://AzureADTokenExchange&mi_res_id=\${MANAGED_IDENTITY_RESOURCE_ID}"
ACCESS_TOKEN="$(az rest --method get --url $IMDS_URL --headers Metadata=true --query access_token -o tsv)"
az login --service-principal --username "${overrideAppId || backupApps?.backupAppId || ''}" --tenant "${scanningAccount?.tenantId}" --federated-token "$ACCESS_TOKEN"

# Download and install EON Agent
mkdir -p /var/lib/eon/
az storage blob download --auth-mode login --account-name ${agentStorageAccount} --container-name eon-backint-agent --name agent_manager --file /var/lib/eon/agent_manager
az storage blob download --auth-mode login --account-name ${agentStorageAccount} --container-name eon-backint-agent --name agent-manager.service --file /etc/systemd/system/agent-manager.service
sudo chmod +x /var/lib/eon/agent_manager
`}
            blur={!scanningAccount?.tenantId || !managedIdentityResourceId}
          />
          <Typography variant='body2' component='span' className='text-inherit'>
            Set Agent configuration after installation:
          </Typography>
          <CopyableCode
            code={`\
cat <<EOF >/var/lib/eon/agent-manager.json
{
  "auto_update": true,
  "poll_interval": "1m",
  "retry_max_attempts": 3,
  "retry_initial_delay": "1s",
  "retry_max_delay": "30s",
  "retry_multiplier": 2.0,
  "service_log_upload": true,
  "service_log_lookback": "5m",
  "bucket_override": {
    "storage_account": "${agentStorageAccount}",
    "managed_identity": "${managedIdentityResourceId}",
    "tenant_id": "${scanningAccount?.tenantId}",
    "client_id": "${overrideAppId || backupApps?.backupAppId || ''}"
  }
}
EOF
sudo systemctl enable agent-manager
sudo systemctl start agent-manager
`}
            blur={!scanningAccount?.tenantId || !managedIdentityResourceId}
          />
        </Stack>
      </MainDialog>
      <ErrorDialog
        isOpen={!!validationError}
        title="Couldn't validate configuration"
        description='Check the error details and contact support if needed.'
        onClose={onClose}
        error={validationError}
      />
    </>
  );
};
